import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { getFormTemplate } from 'services/taskService'
import { getCategoryLabel } from '../formCategories'
import FormTemplatePickerModal from './FormTemplatePickerModal'

const FormTemplateField = ({
  value,
  template,
  onChange,
  buildingPassportId,
  programmeObligationId,
  allowNone = false,
  noneLabel = 'No form needed',
  disabled = false,
  error,
  emptyLabel = 'No form chosen yet',
  emptyHint = 'Choose the form the person doing this work will fill in.',
  chooseLabel = 'Choose a form',
  changeLabel = 'Change form',
  modalTitle,
  modalDescription
}) => {
  const [open, setOpen] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)
  const [resolved, setResolved] = useState(template || null)
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    if (!value) {
      setResolved(null)
      setUnavailable(false)
      return
    }
    if (template?.id === value) {
      setResolved(previous => (previous?.id === value ? previous : template))
    }
    if (resolved?.id === value && resolved.buildingScope !== undefined) return

    let cancelled = false
    setUnavailable(false)
    getFormTemplate(value)
      .then(response => {
        if (cancelled) return
        setResolved(response?.data?.data || response?.data || null)
      })
      .catch(() => {
        if (cancelled) return
        setResolved(null)
        setUnavailable(true)
      })
    return () => { cancelled = true }
  }, [value, template, resolved?.id])

  const outOfScope = !!(
    buildingPassportId &&
    resolved?.buildingScope === 'selected' &&
    Array.isArray(resolved?.buildingPassportIds) &&
    !resolved.buildingPassportIds.includes(buildingPassportId)
  )

  const handleSelect = selected => {
    setResolved(selected)
    onChange?.(selected)
  }

  return (
    <>
      <Field $hasError={!!error} $disabled={disabled}>
        <FieldIcon $chosen={!!value}>
          <DescriptionOutlinedIcon style={{ fontSize: 20 }} />
        </FieldIcon>
        <FieldText>
          {value && resolved ? (
            <>
              <FieldName>{resolved.name}</FieldName>
              <FieldMeta>
                {[
                  getCategoryLabel(resolved.category),
                  `${resolved.fieldCount || 0} question${resolved.fieldCount === 1 ? '' : 's'}`
                ].filter(Boolean).join(' · ')}
              </FieldMeta>
            </>
          ) : value && unavailable ? (
            <>
              <FieldName>A form is set, but you cannot open it</FieldName>
              <FieldMeta>
                The person who owns this form has not shared it with you. Choose a different form,
                or ask them to share it.
              </FieldMeta>
            </>
          ) : value ? (
            <FieldName>Loading the chosen form…</FieldName>
          ) : (
            <>
              <FieldName $muted>{allowNone ? noneLabel : emptyLabel}</FieldName>
              <FieldMeta>{emptyHint}</FieldMeta>
            </>
          )}
        </FieldText>
        <FieldActions>
          {value && resolved && (
            <SecondaryButton type='button' onClick={() => { setPreviewMode(true); setOpen(true) }} disabled={disabled}>
              <VisibilityOutlinedIcon style={{ fontSize: 18 }} />
              Preview
            </SecondaryButton>
          )}
          <PrimaryButton type='button' onClick={() => { setPreviewMode(false); setOpen(true) }} disabled={disabled}>
            {value ? changeLabel : chooseLabel}
          </PrimaryButton>
        </FieldActions>
      </Field>
      {error && <ErrorText>{error}</ErrorText>}
      {!error && outOfScope && (
        <WarningText role='alert'>
          This form is not set up for the building you have chosen. Pick a different form, or add
          this building to the form first.
        </WarningText>
      )}

      <FormTemplatePickerModal
        open={open}
        onClose={() => { setOpen(false); setPreviewMode(false) }}
        value={value}
        onSelect={handleSelect}
        buildingPassportId={buildingPassportId}
        allowNone={allowNone}
        noneLabel={noneLabel}
        initialPreviewTemplate={previewMode ? resolved : null}
        programmeObligationId={programmeObligationId}
        {...(modalTitle ? { title: modalTitle } : {})}
        {...(modalDescription ? { description: modalDescription } : {})}
      />
    </>
  )
}

export default FormTemplateField

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 1px solid ${({ $hasError }) => ($hasError ? '#b00020' : '#dedee5')};
  border-radius: 10px;
  background: ${({ $disabled }) => ($disabled ? '#f6f6f8' : '#fff')};
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
`

const FieldIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${({ $chosen }) => ($chosen ? '#e6f7f9' : '#f1f1f4')};
  color: ${({ $chosen }) => ($chosen ? '#33becc' : '#7a7a85')};
`

const FieldText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 160px;
`

const FieldName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $muted }) => ($muted ? '#6b6b76' : '#1c1c22')};
`

const FieldMeta = styled.span`
  font-size: 14px;
  line-height: 1.45;
  color: #7a7a85;
`

const FieldActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const PrimaryButton = styled.button`
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  padding: 0 14px;
  border: 1px solid #dedee5;
  border-radius: 8px;
  background: #fff;
  color: #3d3d47;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const WarningText = styled.span`
  display: block;
  margin-top: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #ffedd5;
  color: #c2410c;
  font-size: 14px;
  line-height: 1.5;
`

const ErrorText = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 14px;
  color: #b00020;
`
